/**
 * InstallationPicker - Select and connect GitHub App installations.
 */

import { useEffect, useState } from "react";
import { useGitStore } from "@/stores/gitStore";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ExternalLink, Building2, User, AlertTriangle, RefreshCw } from "lucide-react";

interface InstallationPickerProps {
  workspaceId: number;
}

export function InstallationPicker({ workspaceId }: InstallationPickerProps) {
  const {
    availableInstallations,
    connectedInstallations,
    isLoading,
    isSaving,
    error,
    fetchAvailableInstallations,
    connectInstallation,
    clearError,
  } = useGitStore();

  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    fetchAvailableInstallations();
  }, [fetchAvailableInstallations]);

  const handleConnect = async () => {
    if (selectedId) {
      await connectInstallation(workspaceId, selectedId);
    }
  };

  // Filter out already connected installations
  const connectedIds = new Set(connectedInstallations.map((i) => i.installationId));
  const available = availableInstallations.filter((i) => !connectedIds.has(i.installationId));

  const handleRetry = () => {
    clearError();
    fetchAvailableInstallations();
  };

  // Show error state if fetch failed
  if (error && !isLoading && availableInstallations.length === 0) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <p className="mb-2">Failed to load GitHub installations: {error.message}</p>
            <Button variant="outline" size="sm" onClick={handleRetry}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
        <p className="text-xs text-center text-muted-foreground">
          Make sure you have{" "}
          <a
            href="https://github.com/apps/factory-dev/installations/new"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            installed the Colony App
          </a>{" "}
          on GitHub.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (available.length === 0) {
    // Check if all installations are already connected
    const allConnected = availableInstallations.length > 0 && connectedInstallations.length > 0;

    return (
      <div className="space-y-4 py-2">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <p className="font-medium mb-2">
              {allConnected
                ? "All available installations are already connected."
                : "GitHub App selection coming soon"}
            </p>
            {!allConnected && (
              <p className="text-sm mb-3">
                GitHub App authentication requires GitHub OAuth integration which is not yet available.
                For now, please use a <strong>Personal Access Token</strong> instead.
              </p>
            )}
          </AlertDescription>
        </Alert>

        {!allConnected && (
          <div className="text-center space-y-3">
            <p className="text-xs text-muted-foreground">
              Select "Personal Access Token" above to connect using a PAT.
            </p>
            <p className="text-xs text-muted-foreground">
              Or{" "}
              <a
                href="https://github.com/apps/factory-dev/installations/new"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                install the Colony App
              </a>{" "}
              and we'll notify you when OAuth is ready.
            </p>
          </div>
        )}

        {allConnected && (
          <Button variant="outline" asChild className="w-full">
            <a
              href="https://github.com/apps/factory-dev/installations/new"
              target="_blank"
              rel="noopener noreferrer"
              className="gap-2"
            >
              Install on More Repositories
              <ExternalLink className="w-4 h-4" />
            </a>
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <RadioGroup
        value={selectedId?.toString() || ""}
        onValueChange={(v) => setSelectedId(parseInt(v, 10))}
      >
        <div className="space-y-2">
          {available.map((installation) => (
            <div
              key={installation.installationId}
              className="flex items-center space-x-3 rounded-lg border p-4 cursor-pointer hover:bg-muted/50"
              onClick={() => setSelectedId(installation.installationId)}
            >
              <RadioGroupItem
                value={installation.installationId.toString()}
                id={`installation-${installation.installationId}`}
              />
              <Label
                htmlFor={`installation-${installation.installationId}`}
                className="flex-1 cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  {installation.accountAvatarUrl ? (
                    <img
                      src={installation.accountAvatarUrl}
                      alt={installation.accountLogin}
                      className="w-8 h-8 rounded-full"
                    />
                  ) : installation.accountType === "Organization" ? (
                    <Building2 className="w-8 h-8 text-muted-foreground" />
                  ) : (
                    <User className="w-8 h-8 text-muted-foreground" />
                  )}
                  <div>
                    <div className="font-medium">{installation.accountLogin}</div>
                    <div className="text-sm text-muted-foreground">
                      {installation.accountType} - {installation.reposCount || 0} repos
                    </div>
                  </div>
                </div>
              </Label>
            </div>
          ))}
        </div>
      </RadioGroup>

      <Button
        onClick={handleConnect}
        disabled={!selectedId || isSaving}
        className="w-full"
      >
        {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        Connect Selected Installation
      </Button>

      <p className="text-xs text-center text-muted-foreground">
        Don't see your organization?{" "}
        <a
          href="https://github.com/apps/factory-dev/installations/new"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          Install Colony App
        </a>
      </p>
    </div>
  );
}
